import { useState } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line no-unused-vars
import { FaSeedling, FaClock } from 'react-icons/fa';

const StakeSelector = ({
  decayLots,
  totalStake,
  lotStakes,
  onTotalChange,
  onLotChange,
  helpText = '減衰期が近いトークンから自動的に割り当てられます',
  showHelpText = true,
  hideTitle = false,
  mode = 'stake',
  readOnly = false,
  customTitle = null,
  hideTotalInput = false,
  excludeTokenTypes = [], // ステーク除外トークンタイプ（例: ['TT', 'TS']）
  showMaxLimit = false, // 上限表示フラグ
  ownedProjects = [], // 所有プロジェクト一覧
  ownedStores = [], // 所有ストア一覧
  onMetadataChange = null, // メタデータ変更ハンドラ
}) => {
  const [displayCount, setDisplayCount] = useState(10);
  const [selectedType, setSelectedType] = useState(null); // 'project' or 'store'
  const [selectedId, setSelectedId] = useState(null);

  // 減衰状態の視覚化
  const getDecayStatus = (lot) => {
    // TSトークンは減衰しない（永久保存）
    if (lot.token_type === 'TS') {
      return { color: 'text-yellow-600', icon: <FaSeedling />, label: '∞' };
    }
    
    if (!lot.decay_end_date) return { color: 'text-green-600', icon: <FaSeedling />, label: '新鮮' };
    
    const now = new Date();
    const decayEnd = new Date(lot.decay_end_date);
    const daysUntilDecay = Math.ceil((decayEnd - now) / (1000 * 60 * 60 * 24));

    if (daysUntilDecay < 0) {
      return { color: 'text-gray-400', icon: <FaClock />, label: '済' };
    } else if (daysUntilDecay <= 2) {
      return { color: 'text-red-600', icon: <FaClock />, label: `🔥 ${daysUntilDecay}` };
    } else if (daysUntilDecay <= 5) {
      return { color: 'text-orange-500', icon: <FaClock />, label: `⚠️ ${daysUntilDecay}` };
    } else {
      return { color: 'text-green-600', icon: <FaSeedling />, label: `✅ ${daysUntilDecay}` };
    }
  };

  // トークンタイプのアイコン取得
  const getTokenIcon = (tokenType) => {
    const icons = {
      QT: { from: '🌍', to: '🌱' },
      DT: { from: '🌱', to: '🌳' },
      TT: { from: '🌳', to: '🌸' },
      IT: { from: '🌸', to: '🍎' },
      XT: { from: '🍎', to: '🦌' },
      CT: { from: '🦌', to: '🫚' },
      RT: { from: '🫚', to: '🍄' },
      ET: { from: '🍄', to: '🌍' },
      TS: { from: '🪙', to: '🪙' },
    };
    return icons[tokenType] || { from: '💰', to: '💰' };
  };

  // ステーク可能なロット（除外トークンタイプをフィルタ）
  const stakeableLots = decayLots.filter(lot => !excludeTokenTypes.includes(lot.token_type));

  // ステーク可能な上限額
  const maxStakeable = stakeableLots.reduce((sum, lot) => sum + (parseFloat(lot.current_amount) || 0), 0);

  // 総量から各ロットへ自動配分
  const distributeStake = (total) => {
    const newLotStakes = {};
    let remaining = total;
    
    // 除外トークンを除いて、減衰が近い順にソート（decay_end_dateがnullは最後）
    const sortedLots = [...stakeableLots]
      .sort((a, b) => {
        const dateA = a.decay_end_date ? new Date(a.decay_end_date) : new Date('9999-12-31');
        const dateB = b.decay_end_date ? new Date(b.decay_end_date) : new Date('9999-12-31');
        return dateA - dateB;
      });
    
    for (const lot of sortedLots) {
      if (remaining <= 0) break;
      const needed = lot.current_amount || 0;
      const allocated = Math.min(needed, remaining);
      newLotStakes[lot.transaction_id] = allocated;
      remaining -= allocated;
    }
    
    return newLotStakes;
  };

  // 全角数字を半角に変換
  const toHalfWidth = (str) => {
    return str.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
  };

  // 総量変更ハンドラ（内部処理）
  const handleTotalStakeInput = (value) => {
    if (value === '' || value === null) {
      onTotalChange(0);
    } else {
      // 全角数字を半角に変換してから処理
      const halfWidth = toHalfWidth(value);
      const numValue = parseFloat(halfWidth) || 0;
      onTotalChange(numValue);
    }
  };

  // 総量確定時の自動配分（フォーカスアウト時）
  const handleTotalStakeBlur = () => {
    const newLotStakes = distributeStake(totalStake);
    Object.entries(newLotStakes).forEach(([lotId, amount]) => {
      onLotChange(lotId, amount);
    });
  };

  // プロジェクト・ストア選択変更ハンドラ
  const handleSelectionChange = (type, id) => {
    // 同じものを再度クリックした場合は選択解除
    if (selectedType === type && selectedId === id) {
      setSelectedType(null);
      setSelectedId(null);
      if (onMetadataChange) {
        onMetadataChange({
          for_project_id: null,
          for_store_id: null,
        });
      }
    } else {
      // 新しく選択
      setSelectedType(type);
      setSelectedId(id);
      if (onMetadataChange) {
        onMetadataChange({
          for_project_id: type === 'project' ? id : null,
          for_store_id: type === 'store' ? id : null,
        });
      }
    }
  };

  // Enterキーでのフォーム送信を防止
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur(); // フォーカスを外して自動配分をトリガー
    }
  };

  // 個別ロット変更ハンドラ（内部処理）
  const handleLotStakeInput = (lotId, value) => {
    // 全角数字を半角に変換してから処理
    const halfWidth = toHalfWidth(value);
    const numValue = parseFloat(halfWidth) || 0;
    onLotChange(lotId, numValue);
  };

  // 無限ロード
  const loadMore = () => {
    setDisplayCount((prev) => prev + 10);
  };

  // 表示用のロットリスト（TSは最後、他は減衰が近い順）
  const sortedLots = [...decayLots].sort((a, b) => {
    // TSトークンは常に最後
    if (a.token_type === 'TS' && b.token_type !== 'TS') return 1;
    if (a.token_type !== 'TS' && b.token_type === 'TS') return -1;
    
    // 両方TSの場合はtransaction_idで昇順
    if (a.token_type === 'TS' && b.token_type === 'TS') {
      return a.transaction_id - b.transaction_id;
    }
    
    // 通常トークンは減衰が近い順
    return new Date(a.decay_end_date) - new Date(b.decay_end_date);
  });

  const displayedLots = sortedLots.slice(0, displayCount);
  const hasMore = displayCount < sortedLots.length;

  // ステーク量合計の検証
  const totalAllocated = Object.values(lotStakes).reduce(
    (sum, val) => sum + (parseFloat(val) || 0),
    0
  );
  const isOverAllocated = totalAllocated > totalStake;

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 space-y-4">
      {/* ヘルプテキスト */}
      {showHelpText && helpText && (
        <p className="text-sm text-gray-600">{helpText}</p>
      )}

      {/* 総量入力 */}
      {!hideTotalInput && (
        <div className="space-y-2">
          {!hideTitle && (
            <label className="block text-sm font-bold text-gray-900">
              {customTitle || (mode === 'payment' ? '支払い総額（SH）' : 'ステーク総量（SH）')}
            </label>
          )}
          <div className="relative">
            <input
              type="number"
              min="0"
              max={showMaxLimit ? maxStakeable : undefined}
              step="1"
              value={totalStake === 0 ? '' : totalStake}
              onChange={(e) => handleTotalStakeInput(e.target.value)}
              onBlur={handleTotalStakeBlur}
              onKeyDown={handleKeyDown}
              placeholder={mode === 'payment' ? '支払い総額' : 'ステーク総量を入力'}
              className="w-full px-4 py-2 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              style={{ imeMode: 'disabled' }}
              readOnly={readOnly}
              disabled={readOnly}
            />
            {showMaxLimit && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                / {maxStakeable.toFixed(1)} SH
              </div>
            )}
          </div>
          {showMaxLimit && totalStake > maxStakeable && (
            <p className="text-sm text-red-600">
              ⚠️ ステーク可能上限を超えています（最大: {maxStakeable.toFixed(1)} SH）
            </p>
          )}
          {isOverAllocated && (
            <p className="text-sm text-red-600">
              ⚠️ 配分量が総量を超えています
            </p>
          )}
        </div>
      )}

      {/* プロジェクト・ストア選択セクション */}
      {(ownedProjects.length > 0 || ownedStores.length > 0) && !readOnly && (
        <div className="border-t border-gray-300 pt-4 space-y-3">
          <div className="text-sm font-bold text-gray-900">
            このステークは誰かの為ですか？
          </div>
          
          <div className="space-y-2">
            {ownedProjects.length > 0 && (
              <>
                <div className="text-xs font-medium text-gray-600">プロジェクト</div>
                {ownedProjects.map((project) => (
                  <label key={`project-${project.project_id}`} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedType === 'project' && selectedId === project.project_id}
                      onChange={() => handleSelectionChange('project', project.project_id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-800">
                      「{project.title}」の為
                    </span>
                  </label>
                ))}
              </>
            )}

            {ownedStores.length > 0 && (
              <>
                <div className="text-xs font-medium text-gray-600 mt-3">ストア</div>
                {ownedStores.map((store) => (
                  <label key={`store-${store.store_id}`} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedType === 'store' && selectedId === store.store_id}
                      onChange={() => handleSelectionChange('store', store.store_id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-800">
                      「{store.store_name}」の為
                    </span>
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* 個別ロット調整セクション */}
      {decayLots.length > 0 && (
        <div className="border-t border-gray-300 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-gray-900">
              個別ロット調整（任意）
            </div>
            <span className="text-sm text-gray-600">
              利用可能: {decayLots.length} ロット
            </span>
          </div>

          {/* ヘッダー行 */}
          <div className="flex items-center gap-1 sm:gap-2 px-2 pb-2 text-xs font-medium text-gray-600 border-b border-gray-300">
            <span className="w-10 sm:w-20 flex-shrink-0 text-center">種別</span>
            <div className="h-4 border-l border-gray-300"></div>
            <span className="flex-1 min-w-[3rem] text-right">残高(SH)</span>
            <div className="h-4 border-l border-gray-300"></div>
            <span className="flex-1 min-w-[2.5rem]">減衰(日)</span>
            <div className="h-4 border-l border-gray-300"></div>
            <span className="flex-[2] min-w-0">{mode === 'payment' ? '使用量(SH)' : 'ステーク量(SH)'}</span>
          </div>

          <div className="space-y-2 pt-2">
              {displayedLots.map((lot) => {
                const status = getDecayStatus(lot);
                const rawValue = lotStakes[lot.transaction_id];
                const allocated = rawValue !== undefined && rawValue !== null && rawValue !== '' && rawValue !== 0
                  ? rawValue
                  : '';

                return (
                  <div
                    key={lot.transaction_id}
                    className="bg-gray-50 border border-gray-300 rounded-lg p-2"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      {lot.token_type === 'TS' ? (
                        <div className="flex items-center justify-center gap-1 flex-shrink-0 w-10 sm:w-20 whitespace-nowrap">
                          <span className="hidden sm:inline text-base">🪙</span>
                          <span className="font-bold text-gray-900 text-sm">TS</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-0.5 flex-shrink-0 w-10 sm:w-20 whitespace-nowrap">
                          <span className="hidden sm:inline text-[0.6rem]">{getTokenIcon(lot.token_type).from}</span>
                          <span className="hidden sm:inline text-[0.4rem] text-gray-400">▶</span>
                          <span className="font-bold text-gray-900 text-sm">
                            {lot.token_type}
                          </span>
                          <span className="hidden sm:inline text-[0.4rem] text-gray-400">▶</span>
                          <span className="hidden sm:inline text-[0.6rem]">{getTokenIcon(lot.token_type).to}</span>
                        </div>
                      )}
                      <div className="h-6 border-l border-gray-300"></div>
                      <span className="text-xs text-gray-700 flex-1 min-w-[3rem] text-right">
                        {lot.current_amount || 0}
                      </span>
                      <div className="h-6 border-l border-gray-300"></div>
                      <span className={`text-xs ${status.color} flex-1 min-w-[2.5rem]`}>
                        {status.label}
                      </span>
                      <div className="h-6 border-l border-gray-300"></div>
                      <input
                        type="number"
                        min="0"
                        max={lot.current_amount || 0}
                        step="1"
                        value={allocated}
                        onChange={(e) => handleLotStakeInput(lot.transaction_id, e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="0"
                        className="flex-[2] min-w-0 px-2 py-1 text-sm border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-600"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="off"
                        style={{ imeMode: 'disabled' }}
                      />
                    </div>
                    {allocated !== '' && parseFloat(allocated) > (lot.current_amount || 0) && (
                      <p className="text-xs text-red-600 mt-1">
                        残高超過
                      </p>
                    )}
                  </div>
                );
              })}

              {hasMore && (
                <button
                  type="button"
                  onClick={loadMore}
                  className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 font-bold"
                >
                  さらに表示 ({sortedLots.length - displayCount} 件)
                </button>
              )}
            </div>
        </div>
      )}

      {/* ロットが0件の場合 */}
      {decayLots.length === 0 && (
        <div className="text-center py-4 text-gray-600">
          <p className="text-sm">ステーク可能なトークンがありません</p>
        </div>
      )}
    </div>
  );
};

StakeSelector.propTypes = {
  decayLots: PropTypes.arrayOf(
    PropTypes.shape({
      transaction_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      token_type: PropTypes.string.isRequired,
      current_amount: PropTypes.number,
      decay_end_date: PropTypes.string,
    })
  ).isRequired,
  totalStake: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  lotStakes: PropTypes.object.isRequired,
  onTotalChange: PropTypes.func.isRequired,
  onLotChange: PropTypes.func.isRequired,
  helpText: PropTypes.string,
  showHelpText: PropTypes.bool,
  hideTitle: PropTypes.bool,
  mode: PropTypes.oneOf(['stake', 'payment']),
  readOnly: PropTypes.bool,
  customTitle: PropTypes.string,
  hideTotalInput: PropTypes.bool,
  ownedProjects: PropTypes.arrayOf(
    PropTypes.shape({
      project_id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
    })
  ),
  ownedStores: PropTypes.arrayOf(
    PropTypes.shape({
      store_id: PropTypes.number.isRequired,
      store_name: PropTypes.string.isRequired,
    })
  ),
  onMetadataChange: PropTypes.func,
};

export default StakeSelector;
